<template>
	<div class="px-4">
		<filter-search-input v-model="filters.search" :applied-filters="appliedFilters" outlined dense>
			<template #append-inner>
				<QDatePicker
					label="Start Date"
					inner outlined dense hide-details clearable
					v-model="filters.startDate" :max="filters.endDate"
				/>

				<QDatePicker
					label="End Date"
					inner outlined dense hide-details clearable
					v-model="filters.endDate" :min="filters.startDate"
				/>
			</template>
			<template #append>
				<div class="d-flex align-center gap-2">
					<excel-btn @click="generateExcel"/>
					<v-tooltip v-if="permissions.add" top z-index="200">
						<template #activator="{ on }">
							<v-btn v-on="on" to="/admin/billableItem/new" fab small depressed color="primary">
								<v-icon>mdi-plus</v-icon>
							</v-btn>
						</template>
						<span class="caption">Add new</span>
					</v-tooltip>
				</div>
			</template>

			<q-filter-input
				label="Billable Item Code"
				dense outlined hide-details clearable multiple
				v-model="filters.billableItem" :items="billableItems"
				item-text="code" item-value="id"
				:parentShownItems="shownBillableItems"
				:data-comparer="(x, value) => x.id === value"
			/>

			<q-filter-input
				label="Billable Code"
				dense outlined hide-details clearable multiple
				v-model="filters.billable" :items="billables"
				item-text="code" item-value="id"
				:parentShownItems="shownBillableItems"
				:data-comparer="(x, value) => x.billableId === value"
			/>

			<q-filter-input
				label="Customer"
				outlined dense hide-details single-line clearable multiple
				v-model="filters.customer" :items="customers"
				item-text="name" item-value="id"
				:parentShownItems="shownBillableItems"
				:data-comparer="(x, value) => x.customerId === value"
			/>

			<q-filter-input
				label="Currency"
				dense outlined hide-details clearable multiple
				v-model="filters.currency" :items="currencies"
				item-text="text" item-value="text"
				:parentShownItems="shownBillableItems"
				:data-comparer="(x, value) => x.billableCurrency === value"
			/>

			<v-select
				v-model="filters.status" :items="statuses"
				dense outlined hide-details clearable multiple
				label="Status" item-text="description" item-value="id"
			>
				<template #selection="{ item }">
					<v-btn
						:color="item.color"
						outlined small class="rounded-xl"
						:style="{
							background: $vuetify.theme.isDark ? '' : item.lightColor
						}"
					>{{item.description}}</v-btn>
				</template>
				<template #item="{ item }">
					<v-btn
						:color="item.color"
						outlined small class="rounded-xl"
						:style="{
							background: $vuetify.theme.isDark ? '' : item.lightColor
						}"
					>{{item.description}}</v-btn>
				</template>
			</v-select>
		</filter-search-input>
		
		<div v-if="!listSections.length" class="rounded-lg bordered new-back lighten2 pa-6 text-center text--secondary">
			No billable items found
		</div>

		<div
			v-for="section in listSections"
			:key="section.key"
			class="bi-section"
			:class="{ 'bi-section--unassigned': section.isUnassigned }"
		>
			<div v-if="section.title" class="bi-section__header">
				<span class="bi-section__title">{{ section.title }}</span>
				<v-btn color="primary" icon x-small	style="background: var(--v-newDesignBackground-lighten);">
					<v-icon small color="primary" class="bi-section__icon">mdi-account-remove</v-icon>
				</v-btn>
				<div class="bi-section__line"/>
			</div>

			<div
				v-for="group in section.groups"
				:key="group.key"
				class="customer-group rounded-lg bordered new-back lighten2 mb-4"
			>
				<div class="customer-group__header">
					<span class="customer-group__title">{{ group.name }}</span>
				</div>

				<div class="customer-group__body">
					<div
						v-for="item in group.items"
						:key="item.id"
						class="bi-row"
						:class="{ 'bi-row--clickable': permissions.detail }"
						@click="permissions.detail ? $q.goTo('/admin/billableItem/' + item.id) : null"
					>
						<div class="bi-cell bi-cell--code">
							<div class="bi-label">Billable Item</div>
							<div class="bi-value font-weight-medium">{{ item.code }}</div>
						</div>
						<div class="bi-cell bi-cell--billable">
							<div class="bi-label">Billable</div>
							<div class="bi-value font-weight-medium">{{ item.billableCode }}</div>
						</div>
						<div class="bi-cell bi-cell--date">
							<div class="bi-label">Start Date</div>
							<div class="bi-value">{{ item.startDate | formatDate }}</div>
						</div>
						<div class="bi-cell bi-cell--date">
							<div class="bi-label">End Date</div>
							<div class="bi-value">{{ item.endDate | formatDate }}</div>
						</div>
						<div class="bi-cell bi-cell--currency">
							<div class="bi-label">Currency</div>
							<div class="bi-value d-flex align-center">
								<v-avatar size="22" class="mr-1">
									<v-img :src="require(`../../../assets/currencies/${item.billableCurrency.toLowerCase()}.png`)" />
								</v-avatar>
								<span class="font-weight-medium">{{ item.billableCurrency }}</span>
							</div>
						</div>
						<div class="bi-cell bi-cell--num">
							<div class="bi-label">Hours</div>
							<div class="bi-value">{{ item.hours | number(2) }}</div>
						</div>
						<div class="bi-cell bi-cell--closed">
							<div class="bi-label">Closed</div>
							<div class="bi-value">
								<check-switch :value="item.isClosed"/>
							</div>
						</div>
						<div class="bi-cell bi-cell--num">
							<div class="bi-label">Billed</div>
							<div class="bi-value">{{ currencyFormat(item.total) }}</div>
						</div>
						<div class="bi-cell bi-cell--num">
							<div class="bi-label">Invoiced</div>
							<div class="bi-value">{{ currencyFormat(item.invoiced) }}</div>
						</div>
						<div class="bi-cell bi-cell--num">
							<div class="bi-label">Adjustment</div>
							<div
								class="bi-value"
								:class="{ 'error--text': item.adjustmentAmount < 0, 'success--text': item.adjustmentAmount > 0 }"
							>
								{{ item.adjustmentAmount ? currencyFormat(item.adjustmentAmount) : 'N/A' }}
							</div>
						</div>
						<div class="bi-cell bi-cell--status">
							<div class="bi-label">Status</div>
							<div class="bi-value">
								<v-menu offset-x right open-on-hover>
									<template #activator="{ on }">
										<v-btn
											v-on="on"
											plain small class="rounded-xl status-chip"
											:color="item.statusColor"
											:style="{ background: item.statusLightColor }"
										>{{shortStatusLabel(item)}}</v-btn>
									</template>
									<v-list dense>
										<v-list-item
											v-for="approver in item.approvers.filter(x => x.statusToApprove === 2)"
											:key="approver.id"
										>
											<div
												class="d-flex justify-space-between align-center"
												style="min-width: 20px; width: 100%"
											>
												<span class="mr-2 body-2">
													<user-avatar :user-id="approver.approverId" :size="28" :show-card="false"/>
													{{ approver.approverBlueTag }}
												</span>
												<span :set="status = approverStatus.find(s => s.id == approver.status)">
													<v-chip
														:color="status.lightColor"
														:style="{ color: status.color }"
														small
													>
														{{ status.description }}
													</v-chip>
												</span>
											</div>
										</v-list-item>
									</v-list>
								</v-menu>
							</div>
						</div>
						<div v-if="permissions.actions" class="bi-cell bi-cell--actions" @click.stop>
							<div class="bi-label">Actions</div>
							<div class="bi-value bi-actions">
								<div class="bi-actions__slot">
									<v-tooltip v-if="item.canApprove" top z-index="200">
										<template #activator="{ on }">
											<v-btn v-on="on" color="primary" @click="approve(item, true)" icon x-small>
												<v-icon small>mdi-check</v-icon>
											</v-btn>
										</template>
										<span class="caption">Approve</span>
									</v-tooltip>
								</div>
								<div class="bi-actions__slot">
									<v-tooltip v-if="item.canApprove" top z-index="200">
										<template #activator="{ on }">
											<v-btn v-on="on" color="primary" @click="approve(item, false)" icon x-small>
												<v-icon small>mdi-undo</v-icon>
											</v-btn>
										</template>
										<span class="caption">Send to Draft</span>
									</v-tooltip>
								</div>
								<div class="bi-actions__slot">
									<v-menu left offset-x>
										<template #activator="{ on }">
											<v-btn v-on="on" color="primary" icon x-small>
												<v-icon small>mdi-dots-vertical</v-icon>
											</v-btn>
										</template>
									<v-list dense class="py-1">
										<v-list-item class="pa-1 py-0">
											<v-tooltip right z-index="200">
												<template #activator="{ on }">
													<v-btn v-on="on" color="#ff6347" @click="$adminSurf('billableItemToPdf', item)" icon>
														<v-icon>mdi-file-pdf-box</v-icon>
													</v-btn>
												</template>
												<span class="caption">Export to PDF</span>
											</v-tooltip>
										</v-list-item>
										<v-list-item class="pa-1 py-0">
											<v-tooltip right z-index="200">
												<template #activator="{ on }">
													<v-btn v-on="on" @click="$adminSurf('openHistory', item, 'billableItem')" icon>
														<v-icon>mdi-history</v-icon>
													</v-btn>
												</template>
												<span class="caption">History</span>
											</v-tooltip>
										</v-list-item>
										<v-list-item v-if="!item.canApprove" class="pa-1 py-0">
											<v-tooltip right z-index="200">
												<template #activator="{ on }">
													<v-btn v-on="on" @click="deleteBillableItem(item)" icon color="error">
														<v-icon>mdi-cancel</v-icon>
													</v-btn>
												</template>
												<span class="caption">Cancel</span>
											</v-tooltip>
										</v-list-item>
										<v-list-item class="pa-1 py-0" v-if="[3,4].includes(item.status)">
											<v-tooltip right z-index="200">
												<template #activator="{ on }">
													<v-btn v-on="on" @click="createInvoice(item)" icon>
														<v-icon>mdi-script-text-outline</v-icon>
													</v-btn>
												</template>
												<span class="caption">Create Invoice</span>
											</v-tooltip>
										</v-list-item>
										<v-list-item class="pa-1 py-0">
											<watchers-menu
												@watcher-added="item.subscribers.push($event)"
												@watcher-removed="item.subscribers.splice(item.subscribers.findIndex(x => x.userId == $event), 1)"
												:entity-id="item.id"
												:watchers="item.subscribers"
												required-permission="billableItems"
											>
												<template #default="{ on }">
													<v-btn v-on="on" icon>
														<v-icon>mdi-eye</v-icon>
													</v-btn>
												</template>
											</watchers-menu>
										</v-list-item>
									</v-list>
								</v-menu>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
		<billable-item-delete-dialog ref="deleteDialog"/>
	</div>
</template>

<script>
import moment from "moment"
import BillableItemDeleteDialog from '../../../dialogs/adminSurf/BillableItemDeleteDialog'
import WatchersMenu from "../../admin-surf/WatchersMenu.vue"
import CheckSwitch from '../../CheckSwitch.vue'
import ExcelBtn from "../../ExcelBtn"

export default {
	components: { BillableItemDeleteDialog, WatchersMenu, CheckSwitch, ExcelBtn },
	props: ['customerId', 'billableId', 'invoiced', 'currency'],
	data: () => ({
		billableItems: [],
		billables: [],
		customers: [],
		currencies: [
			{text: 'MXN'},
			{text: 'USD'},
			{text: 'EUR'},
		],
		durationUnits: [
			{ value: 3, hours: 1, text: 'Hours' },
			{ value: 0, hours: 8, text: 'Days' },
			{ value: 1, hours: 40, text: 'Weeks' },
			{ value: 2, hours: 168, text: 'Months' },
		],
		filters: {
			billableItem: [],
			billable: [],
			customer: [],
			currency: [],
			status: [],
			startDate: moment().subtract(1, 'month').startOf('month').format('YYYY-MM-DD'),
		},
		statuses: [],
		approverStatus : [],
		currentUser: {},

		permissions: {
			detail: false,
			add: false,
			actions: false,
		},
	}),
	mounted(){
		document.title = 'Billable Items'

		this.$security.hasRequiredPermission('detail')
			.then(res => this.permissions.detail = res)
		this.$security.hasRequiredPermission('add')
			.then(res => this.permissions.add = res)
		this.$security.hasRequiredPermission('full_access')
			.then(res => this.permissions.actions = res)

    this.$q.log(4, 24, 3, 'NA')
		this.setUserPreferences("BillableItems", "filters")
		if(this.customerId){
			this.filters = {
			  billableItem: [],
			  billable: [],
			  customer: [],
			  currency: [],
				status: [],
				startDate: moment().subtract(1, 'month').startOf('month').format('YYYY-MM-DD'),
			}
		  this.filters.customer.push(this.customerId)

			if(this.invoiced == 'true'){
				this.filters.status.push(1, 2)
			}
			else if(this.invoiced == 'false'){
				this.filters.status.push(3, 4)
			}
			else {
				this.filters.status.push(3, 4, 5)
			}
			if(this.currency){
				this.filters.currency.push(this.currency)
			}
		}
		if(this.billableId){
			this.filters = {
			  billableItem: [],
			  billable: [],
			  customer: [],
			  currency: [],
				status: [],
				startDate: moment().subtract(1, 'month').startOf('month').format('YYYY-MM-DD'),
			}
		  this.filters.billable.push(this.billableId)

			if(this.invoiced == 'true'){
				this.filters.status.push(5)
			}
			else if(this.invoiced == 'false'){
				this.filters.status.push(3, 4)
			}
			else {
				this.filters.status.push(3, 4, 5)
			}
			if(this.currency){
				this.filters.currency.push(this.currency)
			}
		}

		this.init()
	},
	computed: {
		shownBillableItems(){
			let billableItems = this.billableItems

			if(this.filters.billableItem && this.filters.billableItem.length){
				billableItems = billableItems.filter(bi => this.filters.billableItem.includes(bi.id))
			}
			if(this.filters.billable && this.filters.billable.length){
				billableItems = billableItems.filter(bi => this.filters.billable.includes(bi.billableId))
			}
			if(this.filters.customer && this.filters.customer.length){
				billableItems = billableItems.filter(bi => this.filters.customer.includes(bi.customerId))
			}
			if(this.filters.startDate){
				billableItems = billableItems.filter(bi => moment(bi.endDate) >= moment(this.filters.startDate))
			}
			if(this.filters.endDate){
				billableItems = billableItems.filter(bi => moment(bi.startDate) <= moment(this.filters.endDate))
			}
			if(this.filters.currency && this.filters.currency.length){
				billableItems = billableItems.filter(bi => this.filters.currency.includes(bi.billableCurrency))
			}
			if(this.filters.status && this.filters.status.length){
				billableItems = billableItems.filter(bi => this.filters.status.includes(bi.status))
			}

			if(this.filters.search){
				billableItems = billableItems.filter(bi =>
					this.normalizeIncludes(bi.code, this.filters.search) ||
					this.normalizeIncludes(bi.billableCode, this.filters.search) ||
					this.normalizeIncludes(bi.customerName, this.filters.search) ||
					this.normalizeIncludes(this.formatDate(bi.startDate), this.filters.search) ||
					this.normalizeIncludes(this.formatDate(bi.endDate), this.filters.search) ||
					this.normalizeIncludes(bi.billableCurrency, this.filters.search) ||
					this.normalizeIncludes(bi.hours, this.filters.search) ||
					this.normalizeIncludes(bi.total, this.filters.search)
				)
			}

			return billableItems
		},
		appliedFilters(){
			return this.countActiveFilters(this.filters, ['search'])
		},
		customerGroups(){
			const assignedMap = {}
			const unassignedMap = {}

			this.shownBillableItems.forEach(item => {
				const target = item.deliveryManagerId ? assignedMap : unassignedMap
				const key = item.customerId || 'unknown'
				if(!target[key]){
					target[key] = {
						key,
						name: item.customerName || 'Unknown',
						items: [],
					}
				}
				target[key].items.push(item)
			})

			const sortGroups = map => Object.values(map)
				.sort((a, b) => a.name.localeCompare(b.name))

			return {
				assigned: sortGroups(assignedMap),
				unassigned: sortGroups(unassignedMap),
			}
		},
		listSections(){
			const sections = []
			if(this.customerGroups.assigned.length){
				sections.push({
					key: 'assigned',
					title: null,
					isUnassigned: false,
					groups: this.customerGroups.assigned,
				})
			}
			if(this.customerGroups.unassigned.length){
				sections.push({
					key: 'unassigned',
					title: 'Unassigned Customers',
					isUnassigned: true,
					groups: this.customerGroups.unassigned,
				})
			}
			return sections
		},
	},
	methods: {
		async init(){
			await this.$blueSystem.getCurrentUser()
        .then(user => {
          this.currentUser = user
        })
				.catch(error => this.$root.error('Failed to get user'))

				this.$http.get('api/enums/approver/status')
					.then(res => {
						this.approverStatus = res.data
						console.log("🚀 ~ approverStatus:", res.data)
					})

				this.$http.get('api/enums/admin/billableItem/status')
					.then(res => {
						this.statuses = res.data
					})
					.catch(err => {
						console.error(err)
						this.$root.error('Cannot obtain billable item status')
					})

			this.$http.get('api/billableItem?includeDeleted=true')
				.then(res => {
					this.billableItems = res.data
						.map(x => ({
							...x,
							canApprove: x.approvers
								.some(y =>
									!x.isDraft && !x.reviewed &&
									(y.approverId == this.currentUser.id || this.currentUser.isSuperAdmin) &&
									y.statusToApprove === 2 &&
									y.status === 0
								)
						}))
				})
				.catch(err => {
					console.error(err)
					this.$root.error('Cannot obtain billable items')
				})

			this.$http.get('api/Billable')
				.then(res => {
					this.billables = res.data
				})
				.catch(res => {
					window.getApp.error("Cannot obtain billables.")
				})

			this.$http.get("/api/customer?onlyWithCustomerProjects=true")
				.then(res => {
					this.customers = res.data
				})
				.catch(res => {
					window.getApp.error("Cannot obtain customers.")
				})
		},
		createInvoice(item){
			this.$router.push(`/admin/invoice/new?fromBillableItem=${item.id}`)
		},
		shortStatusLabel(item){
			const labels = {
				0: 'N/A',
				1: 'Draft',
				2: 'In Review',
				3: 'Pending',
				4: 'Partially',
				5: 'Invoiced',
				6: 'Canceled',
			}
			return labels[item.status] || item.statusDisplayName || 'N/A'
		},
		approve(item, approved) {
			this.$adminSurf('approveBillableItem', item, approved)
				.then(res => {
					item = Object.assign(item, res.data)
					item.canApprove = false
				})
				.catch(err => {
					console.error(err)
					this.$root.error('Cannot approve billable item')
				})
		},
		deleteBillableItem(item){
			this.$refs.deleteDialog.open(item.id)
				.then(res => {
          if(!res)
						return
          this.$http.delete(`api/billableItem/${item.id}`)
						.then(res => {
							window.getApp.success('Deleted successfully')
							this.init()
						})
						.catch(err => {
							console.log(err)
							window.getApp.error('Cannot delete billable item')
						})
				})
		},
		generateExcel(){
			const excelHeaders = [
				{ name: 'Code', value: x => x.code || '' },
				{ name: 'Billable', value: x => x.billableCode || '' },
				{ name: 'Customer', value: x => x.customerName || '' },
				{ name: 'Start Date', value: x => moment(x.startDate).format('DD/MM/YYYY') || '' },
				{ name: 'End Date', value: x => moment(x.endDate).format('DD/MM/YYYY') || '' },
				{ name: 'Currency', value: x => x.billableCurrency || '' },
				{ name: 'Status', value: x => x.statusDisplayName || '' },
				{ name: 'Hours', value: x => x.hours || '' },
				{ name: 'Closed', value: x => x.isClosed ? 'Yes' : 'No' },
				{ name: 'Billed', value: x => x.total || '' },
				{ name: 'Invoiced', value: x => x.invoiced || '0' },
				{ name: 'Adjustment', value: x => x.adjustmentAmount || '-' },
				{ name: 'Created By', value: x => x.createdByBlueTag || x.createdByFullName || '-' },
				{ name: 'Created At', value: x => x.createdAt ? moment(x.createdAt).format('DD/MM/YYYY') : '-' },
			];
			this.singleSheetExcel(this.shownBillableItems, excelHeaders, 'Billable Items')
		},
	},
}
</script>

<style lang="scss" scoped>
.bi-section {
	&--unassigned {
		margin-top: 8px;
	}

	&__header {
		display: flex;
		align-items: center;
		gap: 10px;
		margin: 8px 0 14px;
	}

	&__title {
		font-size: 0.95rem;
		font-weight: 500;
		color: rgba(0, 0, 0, 0.45);
		white-space: nowrap;
	}

	&__icon {
		flex-shrink: 0;
	}

	&__line {
		flex: 1;
		height: 1px;
		background: rgba(0, 0, 0, 0.12);
	}
}

.customer-group {
	overflow: hidden;
	background: var(--v-newDesignBackground-base);
	box-shadow: none;

	&__header {
		display: flex;
		align-items: baseline;
		gap: 8px;
		padding: 12px 16px 8px;
		border-bottom: none;
	}

	&__title {
		font-weight: 700;
		font-size: 1.05rem;
		line-height: 1.2;
		color: var(--v-newDesignText-base);
	}

	&__body {
		padding: 8px 0;
	}
}

.bi-row {
	display: grid;
	grid-template-columns:
		minmax(120px, 1.2fr)
		minmax(100px, 1fr)
		minmax(90px, 0.85fr)
		minmax(90px, 0.85fr)
		minmax(80px, 0.75fr)
		minmax(60px, 0.65fr)
		minmax(52px, 0.5fr)
		minmax(80px, 0.8fr)
		minmax(80px, 0.8fr)
		minmax(80px, 0.8fr)
		minmax(96px, 0.9fr)
		84px;
	gap: 6px;
	align-items: start;
	padding: 4px 8px 4px 16px;
	border-radius: 0;
	border: none;
	box-shadow: none;

	/* Borders only between billable items, never around a single one */
	& + .bi-row {
		border-top: 1px solid var(--v-newDesignDivider-base, #e4e4e4);
	}

	&--clickable {
		cursor: pointer;

		&:hover {
			background: rgba(0, 0, 0, 0.02);
		}
	}
}

.bi-cell {
	min-width: 0;
}

.bi-label {
	font-size: 0.78rem;
	font-weight: 400;
	color: var(--v-newDesignText-primary);
	opacity: 0.65;
	margin-bottom: 2px;
	white-space: nowrap;
	line-height: 1.2;
}

.bi-value {
	font-size: 0.85rem;
	color: var(--v-newDesignText-base);
	line-height: 1.25;
	word-break: break-word;
	min-height: 28px;
	display: flex;
	align-items: end;
	
}

.bi-cell--actions {
	display: flex;
	flex-direction: column;
	align-items: flex-end;

	.bi-label {
		width: 84px;
		text-align: right;
	}

	.bi-value {
		justify-content: flex-end;
		width: 84px;
	}
}

.bi-actions {
	display: flex;
	align-items: center;
	justify-content: flex-end;
	width: 84px;
	min-width: 84px;
	min-height: 28px;
	margin-left: auto;
}

.bi-actions__slot {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 28px;
	min-width: 28px;
	flex-shrink: 0;
}

.status-chip {
	justify-content: center;
	width: 88px;
	min-width: 88px;
	max-width: 88px;
	height: 24px;
	font-size: 0.75rem;
	font-weight: 600;
	letter-spacing: 0;
	text-transform: none;

	::v-deep .v-btn__content {
		opacity: 1 !important;
	}
}

.project{
	display: inline-block;
	border-radius: 15px;
	margin-right: 5px;
	div{
		display: inline-flex;
		align-items: center;
		justify-content: center;
	}
	.letter{
		width: 30px;
		padding: 5px;
		color: white;
		border-radius: 15px;
	}
	.name{
		padding: 0 10px 0 5px;
		color: black;
	}
}
.bar{
	width: 100%;
	height: 10px;
	background: lightgray;
	border-radius: 5px;
	
	overflow: hidden;
	div{
		height: 20px;
		display: inline-block;
	}
	.no-billable{
		background: var(--v-error-darken);
	}
	.billable{
		background: var(--v-success-base);
	}
}
</style>